import { groupBy, keyBy } from 'lodash';
import { ActionGenerators } from '../Action';
import { ChoiceGenerators } from '../Choice';
import { ClassAccessors } from '../Class';
import { CreatureRuleGenerators } from '../CreatureRule';
import { DataOriginDataInfoKeyEnum, DataOriginGenerators, DataOriginTypeEnum, } from '../DataOrigin';
import { HelperUtils } from '../Helper';
import { ModifierGenerators } from '../Modifier';
import { OptionAccessors, OptionGenerators } from '../Option';
import { RaceAccessors } from '../Race';
import { SpellGenerators } from '../Spell';
import { getComponentId, getComponentTypeId, getCreatureRules, getEntityTypeId, getId, getModifiers, getOptions, getUniqueKey, } from './accessors';
/**
 *
 * @param feat
 */
export function generateDataOriginKey(feat) {
    const componentId = getComponentId(feat);
    const componentTypeId = getComponentTypeId(feat);
    return DataOriginGenerators.generateDataOriginKey(componentId ? componentId : -1, componentTypeId ? componentTypeId : -1);
}
/**
 *
 * @param feats
 * @param optionLookup
 * @param actionLookup
 * @param choiceLookup
 * @param modifierLookup
 * @param spellLookup
 * @param valueLookup
 * @param spellListDataOriginLookup
 * @param ruleData
 */
export function generateBaseFeats(feats, optionLookup, actionLookup, choiceLookup, modifierLookup, spellLookup, valueLookup, spellListDataOriginLookup, ruleData) {
    return feats.map((feat) => generateBaseFeat(feat, optionLookup, actionLookup, choiceLookup, modifierLookup, spellLookup, valueLookup, spellListDataOriginLookup, ruleData));
}
/**
 *
 * @param feat
 * @param optionLookup
 * @param actionLookup
 * @param choiceLookup
 * @param modifierLookup
 * @param spellLookup
 * @param valueLookup
 * @param spellListDataOriginLookup
 * @param ruleData
 */
export function generateBaseFeat(feat, optionLookup, actionLookup, choiceLookup, modifierLookup, spellLookup, valueLookup, spellListDataOriginLookup, ruleData) {
    const id = getId(feat);
    const entityTypeId = getEntityTypeId(feat);
    const actions = ActionGenerators.generateBaseActions(id, entityTypeId, actionLookup, valueLookup, DataOriginTypeEnum.FEAT, feat);
    const choices = ChoiceGenerators.generateBaseChoices(id, entityTypeId, choiceLookup, DataOriginTypeEnum.FEAT, feat);
    const spells = SpellGenerators.generateBaseSpells(id, entityTypeId, spellLookup, DataOriginTypeEnum.FEAT, feat, feat, ruleData, spellListDataOriginLookup, valueLookup);
    const modifiers = ModifierGenerators.generateModifiers(id, entityTypeId, modifierLookup, DataOriginTypeEnum.FEAT, feat, feat);
    const options = generateBaseFeatOptions(feat, actionLookup, spellLookup, optionLookup, modifierLookup, valueLookup, spellListDataOriginLookup, ruleData);
    return Object.assign(Object.assign({}, feat), { actions,
        choices,
        spells,
        modifiers,
        options });
}
/**
 *
 * @param baseFeats
 * @param classes
 * @param race
 */
export function generateAggregatedFeats(baseFeats, classes, race) {
    const feats = [];
    classes.forEach((charClass) => {
        feats.push(...ClassAccessors.getFeats(charClass));
    });
    if (race) {
        feats.push(...RaceAccessors.getFeats(race));
    }
    baseFeats.forEach((feat) => {
        if (getComponentId(feat) === null) {
            feats.push(Object.assign(Object.assign({}, feat), { dataOrigin: DataOriginGenerators.generateDataOrigin(DataOriginTypeEnum.ADHOC, feat) }));
        }
    });
    return feats;
}
/**
 *
 * @param feats
 */
export function generateFeatComponentLookup(feats) {
    return groupBy(feats, (feat) => generateDataOriginKey(feat));
}
/**
 *
 * @param feats
 */
export function generateFeatLookup(feats) {
    return keyBy(feats, (feat) => getId(feat));
}
/**
 *
 * @param feats
 * @param xpInfo
 * @param ruleData
 */
export function generateFeatCreatureRules(feats, xpInfo, ruleData) {
    const rules = [];
    feats.forEach((feat) => {
        const dataOrigin = DataOriginGenerators.generateDataOrigin(DataOriginTypeEnum.FEAT, feat);
        const featureRules = getCreatureRules(feat);
        featureRules.forEach((rule) => {
            rules.push(CreatureRuleGenerators.generateRule(rule, dataOrigin, xpInfo, ruleData));
        });
    });
    return rules;
}
/**
 *
 * @param feat
 * @param actionLookup
 * @param spellLookup
 * @param optionLookup
 * @param modifierLookup
 * @param valueLookup
 * @param spellListDataOriginLookup
 * @param ruleData
 */
export function generateBaseFeatOptions(feat, actionLookup, spellLookup, optionLookup, modifierLookup, valueLookup, spellListDataOriginLookup, ruleData) {
    const featId = getId(feat);
    const featEntityTypeId = getEntityTypeId(feat);
    const baseOptions = OptionGenerators.generateDataOriginBaseOptions(featId, featEntityTypeId, optionLookup, DataOriginTypeEnum.FEAT, feat);
    return OptionGenerators.generateOptions(baseOptions, DataOriginTypeEnum.FEAT, {}, actionLookup, spellLookup, optionLookup, modifierLookup, valueLookup, spellListDataOriginLookup, ruleData, feat);
}
/**
 *
 * @param id
 * @param entityTypeId
 * @param featLookup
 * @param dataOriginType
 * @param primary
 * @param parent
 */
export function generateDataOriginFeats(id, entityTypeId, featLookup, dataOriginType, primary, parent = null) {
    const feats = HelperUtils.lookupDataOrFallback(featLookup, DataOriginGenerators.generateDataOriginKey(id, entityTypeId));
    if (!feats) {
        return [];
    }
    return feats.map((feat) => (Object.assign(Object.assign({}, feat), { dataOrigin: DataOriginGenerators.generateDataOrigin(dataOriginType, primary, parent) })));
}
/**
 *
 * @param feats
 */
export function generateFeatModifiers(feats) {
    const modifiers = [];
    if (feats.length === 0) {
        return modifiers;
    }
    feats.forEach((feat) => {
        modifiers.push(...getModifiers(feat));
        getOptions(feat).forEach((option) => {
            modifiers.push(...OptionAccessors.getModifiers(option));
        });
    });
    return modifiers;
}
/**
 *
 * @param feats
 */
export function generateRefFeatData(feats) {
    let data = {};
    feats.forEach((feat) => {
        data[getUniqueKey(feat)] = {
            [DataOriginDataInfoKeyEnum.PRIMARY]: feat,
            [DataOriginDataInfoKeyEnum.PARENT]: null,
        };
    });
    return data;
}
